import { useTranslation } from 'react-i18next';
import type { TrackState } from '@/shared/types/api/tracking';
import { CLIENT_STATUS_VALUES, type ClientStatus } from '@/shared/types/api/parcels';
import { StatusChip } from '@/shared/ui/chips/StatusChip';

type TrackStateChipProps = {
  state: TrackState;
  partyStatus?: string;
  className?: string;
};

const STATE_STYLES: Record<TrackState, string> = {
  not_found: 'bg-red-50 text-red-700 border-red-200',
  waiting: 'bg-amber-100 text-amber-800 border-amber-200',
  collecting: 'bg-sky-100 text-sky-800 border-sky-200',
  in_party: 'bg-indigo-100 text-indigo-800 border-indigo-200',
};

const CLIENT_STATUS_SET = new Set<string>(CLIENT_STATUS_VALUES);

const isClientStatusCode = (value: string): value is ClientStatus =>
  CLIENT_STATUS_SET.has(value);

export const TrackStateChip = ({ state, partyStatus, className }: TrackStateChipProps) => {
  const { t } = useTranslation('tracking');
  const { t: tClientStatus } = useTranslation('clientStatus');
  // When the backend reports `in_party`, `partyStatus` is the raw workflow
  // enum code (IN_PROGRESS / SORTING / COLLECTING / …). Translate it via the
  // shared clientStatus namespace; fall back to the raw string if we ever
  // receive an unknown code so we don't render an empty chip.
  let label: string;
  if (state === 'in_party' && partyStatus) {
    label = isClientStatusCode(partyStatus) ? tClientStatus(partyStatus) : partyStatus;
  } else {
    label = t(`state.${state}`);
  }
  return <StatusChip toneClass={STATE_STYLES[state]} label={label} className={className} />;
};
